import { TARGET_CHANNELS, TARGET_SAMPLE_RATE } from '../audio/types'
import { generateOmf } from '../omf'
import type { ToyDetails } from '../components/MusicPlayer/ToyDetailsModal'
import {
  fetchFirmware,
  type FirmwareTarget,
} from './fetchFirmware'
import type { S3Io } from './s3'
import { appendToyToIndex } from './toyIndex'
import {
  buildInitialVersionsIndex,
  INITIAL_TOY_VERSION,
  saveVersionsIndex,
} from './toyVersions'
import {
  uploadFiles,
  type UploadFile,
  type UploadProgress,
} from './uploadAudio'

export const MANIFEST_FILENAME = 'manifest.omf'
export const METADATA_FILENAME = 'metadata.json'
export const AUDIO_CONFIG_FILENAME = 'audio.txt'
export const INSTALLER_BASE_URL = 'https://installer.oroblanco.app'

export const AUDIO_CONFIG_TEXT = `${TARGET_CHANNELS === 1 ? 'MONO' : 'STEREO'}\n${TARGET_SAMPLE_RATE}\n`

export type ToyMetadata = {
  id: string
  version: string
  // ISO-8601 timestamp of when this version's metadata was created.
  creationTime: string
  creatorId: string
  creatorName: string
  title: string
  description: string
  manifestFilename: string
  manifestUrl: string
  thumbnailUrl?: string
  firmwareMetadata: Record<string, unknown>
  [extra: string]: unknown
}

export type PackagingState =
  | { status: 'preparing'; folder: string; step: string }
  | { status: 'uploading'; folder: string; progress: UploadProgress }
  | { status: 'done'; folder: string; count: number }
  | { status: 'error'; message: string }

export function pickAppName(
  metadata: Record<string, unknown>,
  fallback: string,
): string {
  for (const k of ['app_name', 'name', 'application'] as const) {
    const v = metadata[k]
    if (typeof v === 'string' && v.length > 0) return v
  }
  return fallback
}

export function buildToyMetadata(args: {
  id: string
  version: string
  creatorId: string
  creatorName: string
  details: ToyDetails
  firmwareMetadata: Record<string, unknown>
  extra?: Record<string, unknown>
}): ToyMetadata {
  const { id, version, creatorId, creatorName, details, firmwareMetadata, extra } = args
  const meta: ToyMetadata = {
    id,
    version,
    creationTime: new Date().toISOString(),
    creatorId,
    creatorName,
    title: details.title,
    description: details.description,
    manifestFilename: MANIFEST_FILENAME,
    manifestUrl: `${INSTALLER_BASE_URL}/${creatorId}/${id}/${version}/${MANIFEST_FILENAME}`,
    firmwareMetadata,
  }
  if (details.thumbnailUrl) meta.thumbnailUrl = details.thumbnailUrl
  if (extra) {
    for (const [k, v] of Object.entries(extra)) meta[k] = v
  }
  return meta
}

export type PackageAndUploadArgs = {
  io: S3Io
  creatorId: string
  creatorName: string
  details: ToyDetails
  // Per-toy files bundled into the manifest (mp3 audio for the Music
  // Player + ABC, .mid files for the MIDI Synth, arbitrary blobs for the
  // admin generic-app tool). Uploaded as-is alongside the firmware.
  payloadFiles: UploadFile[]
  firmwareTarget: FirmwareTarget
  appNameFallback: string
  extraMetadata?: Record<string, unknown>
  onState: (s: PackagingState) => void
}

export async function packageAndUploadToy(
  args: PackageAndUploadArgs,
): Promise<{ folder: string; count: number }> {
  const {
    io,
    creatorId,
    creatorName,
    details,
    payloadFiles,
    firmwareTarget,
    appNameFallback,
    extraMetadata,
    onState,
  } = args

  const folder = crypto.randomUUID()
  // Every creation is a brand-new toy, so its files always land in the
  // initial version's folder: {creatorId}/{folder}/{version}/...
  const version = INITIAL_TOY_VERSION

  onState({ status: 'preparing', folder, step: 'Fetching firmware…' })
  const firmware = await fetchFirmware(io, firmwareTarget)

  onState({ status: 'preparing', folder, step: 'Generating manifest…' })
  const toBytes = async (f: UploadFile) => ({
    path: f.name,
    bytes: new Uint8Array(await f.blob.arrayBuffer()),
  })
  const payloadBytes = await Promise.all(payloadFiles.map(toBytes))
  const audioConfigBytes = new TextEncoder().encode(AUDIO_CONFIG_TEXT)
  const manifestText = await generateOmf({
    appName: pickAppName(firmware.metadata, appNameFallback),
    files: [
      { path: firmware.binaryName, bytes: firmware.binary },
      { path: AUDIO_CONFIG_FILENAME, bytes: audioConfigBytes },
      ...payloadBytes,
    ],
  })

  const metadata = buildToyMetadata({
    id: folder,
    version,
    creatorId,
    creatorName,
    details,
    firmwareMetadata: firmware.metadata,
    extra: extraMetadata,
  })
  const metadataText = JSON.stringify(metadata, null, 2)

  const firmwareBlob = new Blob([firmware.binary as BlobPart], {
    type: 'application/octet-stream',
  })
  const audioConfigBlob = new Blob([AUDIO_CONFIG_TEXT], { type: 'text/plain' })
  const manifestBlob = new Blob([manifestText], { type: 'text/plain' })
  const metadataBlob = new Blob([metadataText], { type: 'application/json' })

  // Upload assets first, manifest before metadata.json (which signals
  // "toy is fully ready" to anything watching the catalog folder).
  const allFiles: UploadFile[] = [
    { name: firmware.binaryName, blob: firmwareBlob },
    { name: AUDIO_CONFIG_FILENAME, blob: audioConfigBlob },
    ...payloadFiles,
    { name: MANIFEST_FILENAME, blob: manifestBlob },
    { name: METADATA_FILENAME, blob: metadataBlob },
  ]

  onState({
    status: 'uploading',
    folder,
    progress: { completed: 0, total: allFiles.length, currentName: allFiles[0].name },
  })
  await uploadFiles({
    files: allFiles,
    prefix: `${folder}/${version}`,
    credentials: io.credentials,
    region: io.region,
    identityId: creatorId,
    onProgress: (progress) => onState({ status: 'uploading', folder, progress }),
  })

  onState({ status: 'preparing', folder, step: 'Updating your toy catalog…' })
  // Written after the version's files so it doubles as the toy-level
  // "this version is complete" marker.
  await saveVersionsIndex(io, creatorId, folder, buildInitialVersionsIndex())
  // Commit the new toy into toy-index.json. appendToyToIndex handles cache drift
  // and concurrent writers (conditional write + retry). Pin it to the id captured
  // for this creation (creatorId) rather than re-reading the active identity: the
  // upload above can take many seconds, during which an admin could stop or switch
  // impersonation, which would otherwise redirect the index write to another folder.
  await appendToyToIndex(io, creatorId, metadata)

  onState({ status: 'done', folder, count: allFiles.length })
  return { folder, count: allFiles.length }
}
